<template>
  <div class="cp-page" :data-type="contentType">
    <HeadNav :selectedLanguage="selectedLanguage" page="gb" :gotodownload="gotodownload" :apk="apk" />

    <main class="cp-main">

      <!-- ========== BREADCRUMB ========== -->
      <BreadcrumbNav variant="hero" style="margin-bottom: 0px;margin-top: 20px;" />

      <!-- ========== HERO ========== -->
      <header class="cp-hero" aria-labelledby="cp-title">
        <div class="cp-hero-bg" aria-hidden="true">
          <div class="cp-orb cp-orb--1"></div>
          <div class="cp-orb cp-orb--2"></div>
          <div class="cp-orb cp-orb--3"></div>
          <div class="cp-grid"></div>
        </div>
        <div class="cp-hero-inner">
          <div class="cp-hero-badge">
            <span class="badge-dot"></span>
            {{ contentType }}
          </div>
          <div class="cp-hero-logo-wrap">
            <img
              class="cp-hero-logo"
              src="@/assets/logo.webp"
              alt="GB WhatsApp"
              width="72"
              height="72"
              loading="eager"
              onerror="this.style.display='none'"
            />
          </div>
          <h1 id="cp-title" class="cp-hero-title">{{ heroTitle || title }}</h1>
          <p v-if="subtitle" class="cp-hero-desc">{{ subtitle }}</p>
          <div class="cp-hero-actions">
            <button v-if="showDownloadButton" class="btn-cp-download" @click="handleDownload">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download APK
            </button>
            <router-link to="/download/" class="btn-cp-ghost">
              View Download Guide
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </router-link>
          </div>
        </div>
      </header>

      <!-- ========== ARTICLE BODY ========== -->
      <div class="cp-article-wrap">

        <!-- 侧边 TOC（桌面端） -->
        <aside class="cp-toc-sticky" v-if="tocItems.length">
          <div class="cp-toc-card">
            <p class="cp-toc-title">On This Page</p>
            <ul class="cp-toc-list">
              <li v-for="item in tocItems" :key="item.id">
                <a :href="`#${item.id}`">{{ item.heading }}</a>
              </li>
            </ul>
            <div class="cp-toc-highlights" v-if="highlightsToShow.length">
              <p class="cp-toc-title">Quick Takeaways</p>
              <ul class="cp-highlights-list">
                <li v-for="(h, i) in highlightsToShow" :key="i">
                  <span class="hl-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  </span>
                  {{ h }}
                </li>
              </ul>
            </div>
          </div>
        </aside>

        <!-- 主文章区 -->
        <article class="cp-article" :class="{ 'cp-article--full': !tocItems.length }">

          <!-- 移动端 TOC -->
          <div class="cp-toc-mobile" v-if="tocItems.length">
            <div class="cp-toc-mobile-bar">
              <span class="cp-toc-title">On This Page</span>
            </div>
            <ul class="cp-toc-mobile-list">
              <li v-for="item in tocItems" :key="item.id">
                <a :href="`#${item.id}`">{{ item.heading }}</a>
              </li>
            </ul>
          </div>

          <!-- Guides 聚合页：博客式卡片入口 -->
          <section
            v-if="hubCards.length"
            class="cp-section cp-section--hub-cards cp-section--var-0"
          >
            <div class="cp-section-header">
              <span class="cp-section-num" aria-hidden="true">00</span>
              <h2 id="guide-topics" class="cp-section-title">Guide topics</h2>
            </div>
            <div class="cp-hub-cards-body">
              <div class="cp-hub-cards-grid">
                <router-link
                  v-for="(card, i) in hubCards"
                  :key="i"
                  :to="card.to"
                  class="cp-hub-card"
                >
                  <div class="cp-hub-card-media" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <div class="cp-hub-card-text">
                    <h3 class="cp-hub-card-title">{{ card.title }}</h3>
                    <p class="cp-hub-card-desc">{{ card.desc }}</p>
                  </div>
                </router-link>
              </div>
            </div>
          </section>

          <!-- 内容章节 -->
          <section
            v-for="(section, index) in sections"
            :key="index"
            class="cp-section"
            :class="`cp-section--var-${index % 4}`"
          >
            <div class="cp-section-header">
              <span class="cp-section-num" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
              <h2 :id="toSectionId(section.heading, index)" class="cp-section-title">
                {{ section.heading }}
              </h2>
            </div>
            <div class="cp-section-body" v-html="section.body"></div>
          </section>

          <!-- Best Practice 提示条 -->
          <section class="cp-section cp-section--tip">
            <div class="cp-tip-card">
              <div class="cp-tip-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 017 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"/></svg>
              </div>
              <div>
                <p class="cp-tip-label">Best Practice</p>
                <p class="cp-tip-text">
                  Use stable builds, keep local backups, and avoid high-risk account behavior.
                  If your account is business-critical or compliance-sensitive, the official app remains the safer default.
                </p>
              </div>
            </div>
          </section>

          <!-- 相关文章 -->
          <section class="cp-section cp-section--related" v-if="links && links.length">
            <div class="cp-section-header">
              <h2 class="cp-section-title">Related Pages</h2>
            </div>
            <div class="cp-related-grid">
              <router-link
                v-for="(link, i) in links"
                :key="i"
                :to="link.to"
                class="cp-related-card"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="13 2 13 9 20 9"/>
                </svg>
                {{ link.label }}
              </router-link>
            </div>
          </section>

          <!-- 页脚卡片 -->
          <footer class="cp-foot">
            <div class="cp-foot-card cp-foot-card--cta">
              <h3>Need the latest stable package?</h3>
              <p>Open the central download page for current build details and installation workflow.</p>
              <router-link to="/download/" class="btn-cp-foot">Go to Download Page</router-link>
            </div>
            <div class="cp-foot-card cp-foot-card--warn">
              <h3>Important Notice</h3>
              <p>GBWhatsApp is unofficial software and is not affiliated with WhatsApp or Meta.</p>
            </div>
          </footer>

        </article>
      </div>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import BreadcrumbNav from '@/components/BreadcrumbNav.vue';

export default {
  name: 'BaseContentPage',
  components: { HeadNav, BreadcrumbNav },
  props: {
    title:       { type: String, required: true },
    /** When set, used for the visible H1 while `title` remains the SEO/document title. */
    heroTitle:   { type: String, default: '' },
    subtitle:    { type: String, default: '' },
    contentType: { type: String, default: 'Knowledge Base' },
    navPage:     { type: String, default: 'download' },
    sections:    { type: Array,  default: () => [] },
    links:       { type: Array,  default: () => [] },
    /** `/guides/` hub: blog-style cards linking to each guide */
    hubCards:    { type: Array,  default: () => [] },
    highlights:  { type: Array,  default: () => [] },
    showDownloadButton: { type: Boolean, default: true },
  },
  data() {
    return {
      from:     'gb',
      filename: 'gb',
      apk:      null,
    };
  },
  computed: {
    selectedLanguage() {
      const name = this.$route?.name || '';
      const lang = name.split('-')[0];
      return ['en', 'es', 'hi', 'pt', 'id'].includes(lang) ? lang : 'en';
    },
    highlightsToShow() {
      return (this.highlights || []).slice(0, 4);
    },
    tocItems() {
      const fromSections = (this.sections || []).map((section, index) => ({
        id:      this.toSectionId(section.heading, index),
        heading: section.heading,
      }));
      if (this.hubCards && this.hubCards.length) {
        return [{ id: 'guide-topics', heading: 'Guide topics' }, ...fromSections];
      }
      return fromSections;
    },
  },
  mounted() {
    this.from     = this.$global.GetQueryString('from')     || this.from;
    this.filename = this.$global.GetQueryString('filename') || this.filename;
    if (this.from) {
      this.getAPK();
    } else {
      this.getOfficalApk();
    }
  },
  methods: {
    toSectionId(heading = '', index = 0) {
      const slug = String(heading)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      return slug || `section-${index}`;
    },
    getAPK() {
      this.$server
        .getAPPInfo({ promotionChannel: this.from, promotionName: this.filename })
        .then((res) => { this.apk = res.code === 200 ? res.data.apk : process.env.VUE_APP_GBLINK; });
    },
    getOfficalApk() {
      this.$server
        .getOfficalApk({ domain: 'gbwhatsapp.chat', appName: 'GB_gbwhatsapp.chat' })
        .then((res) => { this.apk = res.code === 200 ? res.data.apk : process.env.VUE_APP_GBLINK; });
    },
    handleDownload() {
      if (this.apk) this.$global.download(this.apk);
    },
    gotodownload() {
      if (this.apk) this.$global.download(this.apk);
    },
  },
};
</script>

<style lang="scss" scoped>
/* ============================================================
   字体 & 变量 — 与 home.vue 完全对齐
   ============================================================ */
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,400&family=Geist:wght@300;400;500;600;700&display=swap');

$font-display: 'Fraunces', Georgia, serif;
$font-body:   'Geist', system-ui, sans-serif;

$brand:        #0F2347;
$brand-mid:    #1E3A5F;
$brand-soft:   #3B82C4;
$brand-pale:   #EBF2FC;
$brand-light:  #F0F5FB;
$accent:       #F59E0B;
$accent-light: #FCD34D;
$text-main:    #0F2347;
$text-body:    #334155;
$text-muted:   #64748b;
$text-faint:   #94a3b8;
$border:       rgba(30,58,95,0.08);
$border-mid:   rgba(30,58,95,0.14);
$shadow-sm:    0 2px 12px rgba(15,35,71,0.06);
$shadow-md:    0 4px 24px rgba(15,35,71,0.1);
$shadow-lg:    0 16px 48px rgba(15,35,71,0.18);

/* ============================================================
   页面容器
   ============================================================ */
.cp-page {
  font-family: $font-body;
  color: $text-body;
  background: #F0F5FB;
  min-height: 100vh;
}

.cp-main {
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 24px 80px;
}

/* ============================================================
   HERO — 与 home.vue hero 风格呼应（深蓝绿底 + orb 光晕）
   ============================================================ */
.cp-hero {
  position: relative;
    background: #128C7E;
    border-radius: 28px;
    /* margin: 0 -24px 40px; */
    overflow: hidden;
    padding: 80px 24px 56px;
    margin-top: 20px;
    margin-bottom: 20px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      165deg,
      #25D366 0%,
      #128C7E 38%,
      #075E54 72%,
      #064e3b 100%
    );
    z-index: 0;
  }
}

.cp-hero-bg {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

.cp-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);

  &--1 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(37,211,102,0.5) 0%, transparent 68%);
    top: -160px; right: -60px;
    opacity: 0.3;
    animation: orbFloat1 12s ease-in-out infinite;
  }
  &--2 {
    width: 360px; height: 360px;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
    bottom: -100px; left: -50px;
    opacity: 0.18;
    animation: orbFloat2 15s ease-in-out infinite;
  }
  &--3 {
    width: 260px; height: 260px;
    background: radial-gradient(circle, rgba(18,140,126,0.45) 0%, transparent 70%);
    top: 35%; left: 38%;
    opacity: 0.2;
    animation: orbFloat3 10s ease-in-out infinite;
  }
}

@keyframes orbFloat1 {
  0%, 100% { transform: translate(0,0) scale(1); }
  33% { transform: translate(-25px, 25px) scale(1.04); }
  66% { transform: translate(18px,-18px) scale(0.97); }
}
@keyframes orbFloat2 {
  0%, 100% { transform: translate(0,0); }
  50% { transform: translate(35px,-25px); }
}
@keyframes orbFloat3 {
  0%, 100% { opacity: 0.04; }
  50% { opacity: 0.08; }
}

.cp-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 85% 85% at 50% 45%, black 0%, transparent 100%);
  opacity: 0.8;
}

.cp-hero-inner {
  position: relative;
  z-index: 2;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: heroEnter 0.9s cubic-bezier(0.22,1,0.36,1) both;
}

@keyframes heroEnter {
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
}

.cp-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: $font-body;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.9);
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 6px 16px;
  border-radius: 50px;
  margin-bottom: 20px;
}

.badge-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #FCD34D;
  animation: badgePulse 2.5s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes badgePulse {
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(252,211,77,0.4); }
  50% { opacity: 0.8; transform: scale(0.88); box-shadow: 0 0 0 5px rgba(252,211,77,0); }
}

.cp-hero-logo-wrap {
  margin-bottom: 16px;
}

.cp-hero-logo {
  width: 72px; height: 72px;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.25);
  display: block;
}

.cp-hero-title {
  font-family: $font-display;
  font-size: clamp(1.7rem, 4vw, 2.8rem);
  font-weight: 800;
  line-height: 1.12;
  color: #ffffff;
  margin: 0 0 18px;
  letter-spacing: -0.028em;
}

.cp-hero-desc {
  font-size: 15.5px;
  line-height: 1.8;
  color: rgba(255,255,255,0.62);
  margin: 0 auto 32px;
  max-width: 36rem;
  font-weight: 300;
}

.cp-hero-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
}

.btn-cp-download {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background: #ffffff;
  color: #075E54;
  border: none;
  padding: 14px 30px;
  border-radius: 13px;
  font-family: $font-body;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.25,0.46,0.45,0.94);
  box-shadow: 0 5px 22px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.3) inset;

  &:hover {
    background: #f0fdf4;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 10px 36px rgba(0,0,0,0.22);
  }
  &:active { transform: translateY(0); }
}

.btn-cp-ghost {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.88);
  border: 1px solid rgba(255,255,255,0.25);
  padding: 13px 22px;
  border-radius: 13px;
  font-family: $font-body;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.22s ease;

  &:hover {
    background: rgba(255,255,255,0.18);
    border-color: rgba(255,255,255,0.4);
  }
}

/* ============================================================
   文章布局 — 主内容 + 侧边 TOC
   ============================================================ */
.cp-article-wrap {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 28px;
  align-items: start;
}

.cp-article--full {
  grid-column: 1 / -1;
}

/* ============================================================
   侧边 TOC
   ============================================================ */
.cp-toc-sticky {
  position: sticky;
  top: 80px;
}

.cp-toc-card {
  background: #ffffff;
  border: 1px solid $border;
  border-radius: 18px;
  padding: 22px 20px;
  box-shadow: $shadow-sm;
}

.cp-toc-title {
  font-family: $font-body;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $accent;
  margin: 0 0 12px;

  &::before {
    content: '';
    display: block;
    width: 16px;
    height: 2px;
    background: $accent;
    border-radius: 2px;
    margin-bottom: 10px;
  }
}

.cp-toc-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li { margin-bottom: 7px; }

  a {
    font-size: 13px;
    line-height: 1.5;
    color: $text-muted;
    text-decoration: none;
    display: block;
    padding: 4px 8px;
    border-radius: 7px;
    transition: background 0.15s ease, color 0.15s ease;
    border-left: 2px solid transparent;

    &:hover {
      background: $brand-light;
      color: $brand;
      border-left-color: $brand-soft;
    }
  }
}

.cp-toc-highlights {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid $border;
}

.cp-highlights-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    font-size: 12px;
    line-height: 1.55;
    color: $text-body;
    margin-bottom: 8px;
  }

  .hl-icon {
    flex-shrink: 0;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(245,158,11,0.12);
    border: 1px solid rgba(245,158,11,0.2);
    color: $accent;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }
}

/* 移动端 TOC */
.cp-toc-mobile {
  background: #ffffff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: $shadow-sm;
}

.cp-toc-mobile-bar {
  padding: 14px 18px 12px;
  background: $brand-light;
  border-bottom: 1px solid $border;

  .cp-toc-title {
    margin: 0;
    &::before { display: none; }
  }
}

.cp-toc-mobile-list {
  list-style: none;
  margin: 0;
  padding: 10px 18px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  li a {
    font-size: 12.5px;
    color: $brand;
    text-decoration: none;
    background: $brand-light;
    border: 1px solid $border;
    padding: 4px 10px;
    border-radius: 20px;
    transition: background 0.15s ease;

    &:hover { background: $brand-pale; }
  }
}

/* ============================================================
   内容章节
   ============================================================ */
.cp-section {
  background: #ffffff;
  border: 1px solid $border;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: $shadow-sm;
  transition: box-shadow 0.22s ease, transform 0.22s ease;

  &:hover {
    box-shadow: $shadow-md;
    transform: translateY(-2px);
  }

  // 4 种变体，顶部彩条颜色各异
  &--var-0 { border-top: 3px solid #059669; }
  &--var-1 { border-top: 3px solid #3B82C4; }
  &--var-2 { border-top: 3px solid #8B5CF6; }
  &--var-3 { border-top: 3px solid #F59E0B; }
}

.cp-section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid $border;
}

.cp-section-num {
  font-family: $font-display;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: $text-faint;
  flex-shrink: 0;
}

.cp-section-title {
  font-family: $font-display;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  font-weight: 700;
  color: $text-main;
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.cp-section-body {
  padding: 20px 24px 24px;

  :deep(p) {
    font-size: 14.5px;
    line-height: 1.85;
    color: $text-body;
    margin: 0 0 14px;
    &:last-child { margin-bottom: 0; }
  }

  :deep(strong) {
    color: $text-main;
    font-weight: 600;
  }

  :deep(a) {
    color: $brand-soft;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-color: rgba(59,130,196,0.3);
    &:hover { color: $brand; }
  }

  :deep(h3) {
    font-family: $font-display;
    font-size: 1.05rem;
    font-weight: 700;
    color: $text-main;
    margin: 18px 0 8px;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 20px;
    margin: 0 0 14px;
  }

  :deep(li) {
    font-size: 14.5px;
    line-height: 1.75;
    color: $text-body;
    margin-bottom: 6px;
  }
}

/* ============================================================
   提示条
   ============================================================ */
.cp-section--tip {
  border-top: 3px solid $accent;
  border-color: $accent;
}

.cp-tip-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
}

.cp-tip-icon {
  width: 42px; height: 42px;
  border-radius: 11px;
  background: rgba(245,158,11,0.1);
  border: 1px solid rgba(245,158,11,0.2);
  color: $accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cp-tip-label {
  font-family: $font-body;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $accent;
  margin: 0 0 6px;
}

.cp-tip-text {
  font-size: 13.5px;
  line-height: 1.75;
  color: $text-body;
  margin: 0;
}

/* ============================================================
   Guides 聚合：博客式卡片（与 blogs 列表类似的栅格入口）
   ============================================================ */
.cp-section--hub-cards {
  border-top: 3px solid #128C7E;
}

.cp-hub-cards-body {
  padding: 0 24px 24px;
}

.cp-hub-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.cp-hub-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-decoration: none;
  color: inherit;
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: $shadow-sm;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-md;
    border-color: rgba(18, 140, 126, 0.35);
  }
}

.cp-hub-card-media {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  background: linear-gradient(145deg, rgba(37, 211, 102, 0.12) 0%, rgba(18, 140, 126, 0.18) 100%);
  color: #0d9488;
}

.cp-hub-card-text {
  padding: 18px 20px 22px;
  text-align: left;
}

.cp-hub-card-title {
  font-family: $font-display;
  font-size: 1.15rem;
  font-weight: 700;
  color: $text-main;
  margin: 0 0 8px;
  line-height: 1.3;
}

.cp-hub-card-desc {
  font-size: 13.5px;
  line-height: 1.65;
  color: $text-muted;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 768px) {
  .cp-hub-cards-body { padding: 0 18px 18px; }
  .cp-hub-cards-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .cp-hub-card-media { min-height: 88px; }
}

/* ============================================================
   相关文章
   ============================================================ */
.cp-section--related {
  border-top: 3px solid $brand-mid;
}

.cp-related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 20px 24px 24px;
}

.cp-related-card {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  font-weight: 600;
  color: $brand-mid;
  text-decoration: none;
  background: $brand-light;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 11px 14px;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

  svg { flex-shrink: 0; color: $brand-soft; }

  &:hover {
    background: $brand-pale;
    border-color: rgba(30,58,95,0.22);
    color: $brand;
    box-shadow: $shadow-sm;
  }
}

/* ============================================================
   页脚
   ============================================================ */
.cp-foot {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 8px;
}

.cp-foot-card {
  background: #ffffff;
  border: 1px solid $border;
  border-radius: 18px;
  padding: 26px 24px;
  box-shadow: $shadow-sm;

  h3 {
    font-family: $font-display;
    font-size: 1.05rem;
    font-weight: 700;
    color: $text-main;
    margin: 0 0 8px;
  }

  p {
    font-size: 13px;
    line-height: 1.7;
    color: $text-muted;
    margin: 0 0 16px;
  }
}

.btn-cp-foot {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $accent;
  color: #0F2347;
  font-family: $font-body;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 10px;
  transition: background 0.18s ease, transform 0.18s ease;

  &:hover {
    background: #D97706;
    transform: translateY(-1px);
  }
}

.cp-foot-card--warn {
  background: #FFFBEB;
  border-color: rgba(245,158,11,0.22);
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 1024px) {
  .cp-article-wrap {
    grid-template-columns: 1fr;
  }
  .cp-toc-sticky {
    display: none;
  }
}

@media (max-width: 768px) {
  .cp-hero {
    border-radius: 0 0 20px 20px;
    padding: 56px 16px 40px;
    margin: 0 -16px 28px;
  }

  .cp-main { padding: 0 16px 60px; }

  .cp-hero-title { font-size: 1.8rem; }
  .cp-hero-desc { font-size: 14px; }

  .cp-hero-actions { flex-direction: column; gap: 10px; }

  .cp-section-header { padding: 16px 18px 12px; }
  .cp-section-body { padding: 16px 18px 20px; }

  .cp-related-grid { grid-template-columns: 1fr; }

  .cp-foot { grid-template-columns: 1fr; }

  .cp-section:hover {
    transform: none;
    box-shadow: $shadow-sm;
  }
}

</style>
